import React, { useEffect, useRef, useState } from "react";

/** props:
 *  value: { tableTitle, tableRow:[{name,lineType}], tableColumn:[{name,lineType}] }
 *  onChange(nextValue)
 *  forceOpen?: boolean
 *  onAfterOpen?: () => void
 */
export default function PreviewTemplateTableGenerator({
  value,
  onChange,
  forceOpen = false,
  onAfterOpen,
}) {
  const totalRows = 10;
  const totalCols = 10;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hoverRows, setHoverRows] = useState(0);
  const [hoverCols, setHoverCols] = useState(0);
  const [selectedRows, setSelectedRows] = useState(0);
  const [selectedCols, setSelectedCols] = useState(0);

  const [tableName, setTableName] = useState(value?.tableTitle || "");
  const [generated, setGenerated] = useState(false);
  const [cellValues, setCellValues] = useState({});
  const [exported, setExported] = useState(null);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  useEffect(() => {
    if (forceOpen) {
      openModal();
      onAfterOpen?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceOpen]);

  const initCells = (rows, cols) => {
    const next = {};
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) next[`${r}-${c}`] = "";
    }
    return next;
  };

  // ===== Prefill (TOP-LEFT IS COLUMN 1) =====
  useEffect(() => {
    if (!value) return;

    // rows: 1 header row + N data rows (labels live in col 0, rows 1..N)
    const rows = (value.tableRow?.length || 0) + 1;

    // cols: EXACTLY number of column headers (top-left is first header)
    const cols = value.tableColumn?.length || 0;

    if (rows <= 1 || cols <= 0) {
      // not configured yet
      setGenerated(false);
      setTableName(value.tableTitle || "");
      setSelectedRows(0);
      setSelectedCols(0);
      setCellValues({});
      return;
    }

    const base = initCells(rows, cols);

    // top headers (row 0) -> include top-left at 0-0
    value.tableColumn.forEach((c, i) => {
      base[`0-${i}`] = c?.name || "";
    });

    // left labels (col 0) start at row 1
    value.tableRow.forEach((r, i) => {
      base[`${i + 1}-0`] = r?.name || "";
    });

    setSelectedRows(rows);
    setSelectedCols(cols);
    setCellValues(base);
    setTableName(value.tableTitle || "");
    setGenerated(true);
  }, [value]);

  // size picked from modal
  const handleModalUpdate = () => {
    if (hoverRows > 0 && hoverCols > 0) {
      setSelectedRows(hoverRows);
      setSelectedCols(hoverCols);
      setCellValues(initCells(hoverRows, hoverCols));
      setGenerated(true);
      closeModal();
    }
  };

  const handleCellChange = (r, c, v) =>
    setCellValues((prev) => ({ ...prev, [`${r}-${c}`]: v }));

  // ===== Export (TOP-LEFT INCLUDED AS COLUMN 1) =====
  const buildStructuredOutput = () => {
    const tableColumn = [];
    // include 0,0 as first column header
    for (let c = 0; c < selectedCols; c++) {
      const name = (cellValues[`0-${c}`] || "").trim();
      if (name) tableColumn.push({ name, lineType: "line item" });
    }

    const tableRow = [];
    // row labels live in col 0 for rows 1..selectedRows-1
    for (let r = 1; r < selectedRows; r++) {
      const name = (cellValues[`${r}-0`] || "").trim();
      if (name) tableRow.push({ name, lineType: "line item" });
    }

    const payload = {
      tableTitle: tableName || "Untitled Table",
      tableRow,
      tableColumn,
    };

    setExported(payload);
    onChange?.(payload);
  };

  const modalRef = useRef(null);
  useEffect(() => {
    if (isModalOpen && modalRef.current) {
      modalRef.current.querySelector("input[name='tableName']")?.focus();
    }
  }, [isModalOpen]);

  return (
    <div
      style={{
        margin: "8px 0",
        border: "1px solid #e3efe7",
        borderRadius: 12,
        padding: 12,
        backgroundColor: "#fff",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
        <h5 style={{ margin: 0, color: "#205a46" }}>{tableName || "Untitled Table"}</h5>
      </div>

      {generated && (
        <div style={{ marginTop: 12 }}>
          <table
            style={{
              borderCollapse: "collapse",
              width: "100%",
              border: "1px solid #ccc",
            }}
          >
            <tbody>
              {[...Array(selectedRows)].map((_, r) => (
                <tr key={r}>
                  {[...Array(selectedCols)].map((_, c) => {
                    // editable header row (r=0) and first column (c=0)
                    const editable = r === 0 || c === 0;
                    const key = `${r}-${c}`;
                    return (
                      <td
                        key={c}
                        style={{
                          border: "1px solid #ccc",
                          padding: "6px 8px",
                          minWidth: "120px",
                          // background: editable ? "#fff" : "#f8f8f8",
                        }}
                      >
                        {editable ? (
                          <input
                            type="text"
                            value={cellValues[key] ?? ""}
                            onChange={(e) => handleCellChange(r, c, e.target.value)}
                            style={{
                              width: "100%",
                              border: "none",
                              outline: "none",
                              background: "transparent",
                              fontSize: 14,
                              // color: "#222",
                              padding: 0,
                            }}
                            disabled
                          />
                        ) : (
                          <span >{cellValues[key] || "data"}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>

          {exported && (
            <pre
              style={{
                marginTop: 12,
                padding: 12,
                background: "#fafafa",
                border: "1px solid #eee",
                borderRadius: 8,
                overflowX: "auto",
                fontSize: 12,
              }}
            >
{JSON.stringify(exported, null, 2)}
            </pre>
          )}
        </div>
      )}

      {/* Config Modal */}
      {isModalOpen && (
        <div
          aria-modal="true"
          role="dialog"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 99,
            padding: 16,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsModalOpen(false);
          }}
        >
          <div
            ref={modalRef}
            style={{
              width: "min(720px, 96vw)",
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              padding: 20,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <h3 style={{ margin: 0 }}>Configure Table</h3>
              <button
                onClick={() => setIsModalOpen(false)}
                aria-label="Close modal"
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: 22,
                  cursor: "pointer",
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>

            <label htmlFor="tableName" style={{ display: "block", marginBottom: 6 }}>
              Table Name
            </label>
            <input
              id="tableName"
              name="tableName"
              type="text"
              value={tableName}
              onChange={(e) => setTableName(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid #d0d0d0",
                marginBottom: 16,
                backgroundColor: "#f8fff8",
              }}
              disabled
            />

            <div style={{ marginBottom: 6 }}>Table Layout (Rows × Columns)</div>
            <SizePicker
              totalRows={totalRows}
              totalCols={totalCols}
              hoverRows={hoverRows}
              hoverCols={hoverCols}
              selectedRows={selectedRows}
              selectedCols={selectedCols}
              onHover={(r, c) => {
                setHoverRows(r + 1);
                setHoverCols(c + 1);
              }}
              onPick={() => {
                if (hoverRows > 0 && hoverCols > 0) {
                  setSelectedRows(hoverRows);
                  setSelectedCols(hoverCols);
                }
              }}
            />

            <div style={{ marginTop: 8, fontWeight: 500, color: "#333" }}>
              {(selectedRows || hoverRows) > 0 && (selectedCols || hoverCols) > 0
                ? `${selectedRows || hoverRows} Rows × ${selectedCols || hoverCols} Columns`
                : "Select table size"}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
              <button
                onClick={() => setIsModalOpen(false)}
                style={{
                  backgroundColor: "#eaeaea",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 16px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleModalUpdate}
                style={{
                  backgroundColor: "#205a46",
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 16px",
                  cursor: "pointer",
                }}
                disabled={!hoverRows || !hoverCols}
                title={!hoverRows || !hoverCols ? "Pick a size first" : "Create table"}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SizePicker({
  totalRows,
  totalCols,
  hoverRows,
  hoverCols,
  selectedRows,
  selectedCols,
  onHover,
  onPick,
}) {
  return (
    <div
      style={{
        display: "inline-block",
        padding: 10,
        backgroundColor: "#fafafa",
        borderRadius: 10,
        border: "1px solid #e3e3e3",
      }}
    >
      {[...Array(totalRows)].map((_, r) => (
        <div key={r} style={{ display: "flex" }}>
          {[...Array(totalCols)].map((_, c) => {
            const isActive =
              r < (selectedRows || hoverRows) && c < (selectedCols || hoverCols);
            return (
              <div
                key={c}
                onMouseEnter={() => onHover(r, c)}
                onClick={onPick}
                style={{
                  width: 22,
                  height: 22,
                  margin: 2,
                  border: "1px solid #ccc",
                  backgroundColor: isActive ? "#b6e8c4" : "#fff",
                  cursor: "pointer",
                  transition: "background-color 0.15s ease-in-out",
                }}
                title={`${r + 1} × ${c + 1}`}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}
